import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { companiesQueryKeys } from '@/modules/companies/queries/queryKeys';
import { useCreateJobMutation } from '@/modules/jobs/mutations/useCreateJobMutation';
import {
  type JobResponseBody,
  type SubmitIntent,
  toJobCreateRequest,
} from '@/modules/jobs/signal/jobRequestMappers';
import type {
  InboundDraft,
  RoleDraft,
  WorkflowDraft,
} from '@/modules/jobs/signal/types';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

/**
 * The panel→create wiring of the recruiter create page (`JobAdd`, plan 13): maps the drafts to the create request and
 * mutates. Success navigates away, so it toasts; a failure keeps the user on the form, so it comes back as `error` for
 * the editor to show inline. Navigation is the page's.
 */
export const useJobCreateSubmit = (
  onCreated: (job: JobResponseBody | undefined, intent: SubmitIntent) => void,
) => {
  const createJobMutation = useCreateJobMutation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (
    role: RoleDraft,
    workflow: WorkflowDraft,
    intent: SubmitIntent,
    inbound?: InboundDraft,
  ) => {
    setError(null);
    const body = { ...toJobCreateRequest(role, workflow, intent), ...inbound };
    createJobMutation.mutate(body, {
      onSuccess: (createdJob) => {
        if (inbound) {
          // The Workspace's Careers Page section counts the jobs that accept applications.
          void queryClient.invalidateQueries({
            queryKey: companiesQueryKeys.inbound(workflow.companyId),
          });
        }
        toast.success(
          intent === 'publish'
            ? 'Job published. Finlay started looking for candidates.'
            : 'Draft saved',
        );
        onCreated(createdJob, intent);
      },
      onError: (mutationError) =>
        setError(
          actionErrorMessage(
            intent === 'publish' ? 'publish the job' : 'save the draft',
            mutationError,
          ),
        ),
    });
  };

  return { onSubmit, submitting: createJobMutation.isPending, error };
};
